import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LOGO } from "../services/ImageService";
import { useSiteContext } from "../contexts/SiteProvider";
import { checkInterSection } from "../services/core/FilterService";
import { get } from "../services/smartApiService";

interface childrenProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

// ── Variants ───────────────────────────────────────────────────
const navItemVariants = {
  hidden: { opacity: 0, x: -16 },
  show  : (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, delay: i * 0.045, ease: [0.4, 0, 0.2, 1] },
  }),
};

const flyoutVariants = {
  hidden: { opacity: 0, scale: 0.95, y: -4 },
  show  : { opacity: 1, scale: 1,    y: 0,  transition: { duration: 0.18, ease: [0.4, 0, 0.2, 1] } },
  exit  : { opacity: 0, scale: 0.95, y: -4, transition: { duration: 0.13, ease: "easeIn" } },
};

const flyoutItemVariants = {
  hidden: { opacity: 0, x: -8 },
  show  : (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.15, delay: i * 0.05 },
  }),
};

const FLYOUT_ITEM_H = 44;
const FLYOUT_PADDING = 12;

const EFSideNav: React.FC<childrenProps> = ({ isOpen, setIsOpen }) => {
  const { settings } = useSiteContext();
  const sideRef      = useRef<HTMLDivElement | null>(null);
  const navigate     = useNavigate();
  const location     = useLocation();
  const { user }     = useSiteContext();

  const [tabData, setTabData]             = useState<any[]>([]);
  const [openChild, setOpenChild]         = useState<number | null>(null);
  const [flyoutTop, setFlyoutTop]         = useState<number>(0);
  const [flyoutLeft, setFlyoutLeft]       = useState<number>(240);
  const [flyoutChildren, setFlyoutChildren] = useState<any[]>([]);

  // ── Load menu ──────────────────────────────────────────────
  useEffect(() => {
    const sub = get("/admin_menu/get_all_menu").subscribe((res) => {
      setTabData(res.data);
    });
    return () => sub.unsubscribe();
  }, []);

  // ── Close flyout on outside click ─────────────────────────
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // keep open if click is inside sidebar or the flyout itself
      if (
        (sideRef.current && sideRef.current.contains(target)) ||
        target.closest(".child-link")
      ) return;
      setOpenChild(null);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ── Navigate ───────────────────────────────────────────────
  const navigateLink = (link: string) => {
    navigate(link);
    setOpenChild(null);
    if (window.innerWidth <= 768) setIsOpen(false);
  };

  // ── Logo ───────────────────────────────────────────────────
  const getSiteLogo = () => {
    if (settings?.st_site_company_logo) {
      return `data:${settings.st_site_company_logo.type};base64,${settings.st_site_company_logo.content}`;
    }
    return LOGO;
  };

  // ── Toggle child flyout ────────────────────────────────────
  const toggleChild = (
    index: number,
    item: any,
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (openChild === index) {
      setOpenChild(null);
      return;
    }

    const rect       = e.currentTarget.getBoundingClientRect();
    const children   = Array.isArray(item.children) ? item.children : [];
    const flyoutH    = children.length * FLYOUT_ITEM_H + FLYOUT_PADDING;
    const maxTop     = window.innerHeight - flyoutH - 8;
    const sideW      = isOpen ? 240 : 48;

    setFlyoutTop(Math.min(rect.top, maxTop));
    setFlyoutLeft(sideW + 4);          // 4px gap from sidebar edge
    setFlyoutChildren(children);
    setOpenChild(index);
  };

  return (
    <div ref={sideRef} className={`smart-sidenav ${isOpen ? "expanded" : ""}`}>

      {/* Brand Logo */}
      <div className="brand-logo">
        <img src={getSiteLogo()} alt="Brand Logo" />
      </div>

      {/* ── Nav items (no flyout rendered inside these) ─────── */}
      {tabData.map((item: any, index: number) => {
        const _roles: string[] = Array.isArray(item.roles)
          ? item.roles
          : item.roles.split(",");

        if (!checkInterSection(user?.roles || [], _roles)) return null;

        const isActive    = location.pathname === item.link;
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const childOpen   = openChild === index;

        return (
          <motion.a
            key={`nav_link_${index}`}
            href="#"
            className={`sidenav-link${isActive ? " active-link" : ""}${childOpen ? " child-expanded" : ""}`}
            custom={index}
            variants={navItemVariants}
            initial="hidden"
            animate="show"
            whileTap={{ scale: 0.96, transition: { duration: 0.07 } }}
            onClick={(e) => {
              e.preventDefault();
              if (hasChildren) {
                toggleChild(index, item, e);
              } else {
                navigateLink(item.link);
              }
            }}
          >
            <i className={`icon fa ${item.icon}`} />
            <span className="link-text">
              {item.label}
              {hasChildren && (
                <i
                  className={`fa fa-chevron-${childOpen ? "up" : "down"} nav-chevron`}
                />
              )}
            </span>
          </motion.a>
        );
      })}

      {/* ── Child flyout — rendered at sidebar root level ───── */}
      {/* Being outside every motion.a means no event-capture conflict */}
      <AnimatePresence>
        {openChild !== null && flyoutChildren.length > 0 && (
          <motion.ul
            className="child-link"
            style={{ top: flyoutTop, left: flyoutLeft }}
            variants={flyoutVariants}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {flyoutChildren.map((child: any, ci: number) => {
              const childRoles: string[] = Array.isArray(child.roles)
                ? child.roles
                : child.roles.split(",");

              if (!checkInterSection(user?.roles || [], childRoles)) return null;

              const childActive = location.pathname === child.link;

              return (
                <motion.li
                  key={`flyout_child_${ci}`}
                  custom={ci}
                  variants={flyoutItemVariants}
                  initial="hidden"
                  animate="show"
                  whileTap={{ scale: 0.97 }}
                  className={`sidenav-child-link-items${childActive ? " active" : ""}`}
                  onClick={() => navigateLink(child.link)}
                >
                  <span className="icon link-icon">
                    <i className={`fa ${child.icon}`} />
                  </span>
                  <span className="smart-child-label">{child.label}</span>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>

    </div>
  );
};

export default EFSideNav;
